import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react"
import { Form, Pagination } from "react-bootstrap"
import StudentNotificationItem from "./StudentNotificationItem"
import { getStudentNotifications } from "shared/routes/notifications/studentNotifications"
import { NOTIFICATIONS_PER_PAGE, PAGES_PER_SET_NOTIFICATIONS, StudentNotification, StudentNotificationStatus } from "shared/types/notificationTypes"
import { useModel } from "@stem-sims/nexus"
import { BeatLoader } from "react-spinners"

const StudentNotificationsCenter = () => {
  const [showRead, setShowRead] = useState<boolean>(false)
  const [currentPage, setCurrentPage] = useState<number | null>(null)
  const [pageRange, setPageRange] = useState<number[]>([])
  const [pageRangeNumber, setPageRangeNumber] = useState<number>(1)
  const [paginatedNotifications, setPaginatedNotifications] = useState<StudentNotification[]>([]) // this state is used to perform the UI-only refresh when notification state is updated

  const { response: data, refreshModel: refreshAllNotifications, loading } = useModel({
    model: getStudentNotifications,
    props: {
      pageNumber: currentPage,
      showRead,
    }
  })

  const totalPages = useMemo(() => {
      if (!data) return
      const totalPagesNumber: number = Math.ceil(data.filteredCount / NOTIFICATIONS_PER_PAGE)
      return new Array(totalPagesNumber).fill(0).map((_, i) => i + 1)
  }, [data])

  useEffect(() => {
    if (!data) return
    setPaginatedNotifications(data.notifications)
  }, [data, showRead])

  /** Calculate which pages should show in one set */
  useEffect(() => {
    if (!totalPages) return

    const start = (pageRangeNumber - 1 ) * PAGES_PER_SET_NOTIFICATIONS
    const end = start + PAGES_PER_SET_NOTIFICATIONS

    const currentPageRange = totalPages.slice(start, end)
    setPageRange(currentPageRange)
  }, [pageRangeNumber, totalPages])

  useEffect(() => {
    if (pageRange.length === 0 || currentPage !== null) return
    setCurrentPage(pageRange[0])
  }, [pageRange, currentPage])

  const prevPageSet = (currentPageRange: number) => {
    const newPageRange = currentPageRange - 1
    setPageRangeNumber(newPageRange)

    // select the first page as default after switching to a new page set
    const start = (newPageRange - 1 ) * PAGES_PER_SET_NOTIFICATIONS
    const end = start + PAGES_PER_SET_NOTIFICATIONS
    setCurrentPage(totalPages.slice(start, end)[0])
  }

  const nextPageSet = (currentPageRange: number) => {
    const newPageRange = currentPageRange + 1
    setPageRangeNumber(newPageRange)

    // select the first page as default after switching to a new page set
    const start = (newPageRange - 1 ) * PAGES_PER_SET_NOTIFICATIONS
    const end = start + PAGES_PER_SET_NOTIFICATIONS
    setCurrentPage(totalPages.slice(start, end)[0])
  }

  const refreshNotifications = useCallback(async (notificationId: string) => {
    const newNotifications = paginatedNotifications.map(notification => notificationId === notification.id ? 
      { ...notification, status: (notification.status === "read" ? "unread" : "read" as StudentNotificationStatus) } : notification)
      setPaginatedNotifications(newNotifications)
  }, [paginatedNotifications])

  /** Reset all pagination variables when toggle */
  const toggleShowRead = (value: boolean) => {
    setShowRead(value)
    setCurrentPage(null)
    setPageRange([])
    setPageRangeNumber(1)
  }

  const isEmpty = loading || data?.notifications.length === 0

  if (loading || !data) return <BeatLoader />
  return (
    <div>
      <h1 className="my-4 fw-bold">Notifications Center</h1>

      {data.totalCount === 0 && (
        <div className="alert alert-info" role="alert">
          You have no notifications.
        </div>
      )}

      {data.totalCount > 0 && <Form className="text-start text-primary fw-bold mx-4">
          <Form.Check
              onChange={(e: ChangeEvent<HTMLInputElement>) => toggleShowRead(e.currentTarget.checked)}
              checked={showRead}
              type="switch"
              id="hide-read-notifications-switch"
              label={<span>Show All Notifications</span>}
          />
      </Form>}

      {data.totalCount > 0 && data.notifications.length === 0 && (
        <div className="alert alert-info mt-4" role="alert">
          You have no new notifications.
        </div>
      )}

      {(totalPages.length > 1 && !isEmpty) && 
          <Pagination className="mt-3 mx-4">
              {totalPages.length > PAGES_PER_SET_NOTIFICATIONS && 
                  <Pagination.Prev 
                    className="me-2"
                    disabled={pageRangeNumber === 1} 
                    onClick={() => prevPageSet(pageRangeNumber)} />}

              {pageRange.map((pageNumber) => {
                  return (
                      <Pagination.Item
                          className="me-2"
                          onClick={() => {
                            setCurrentPage(pageNumber)
                            refreshAllNotifications()
                          }}
                          key={pageNumber}
                          active={pageNumber === currentPage}
                          data-testid={`page-${pageNumber}`}
                          >
                          {pageNumber}
                      </Pagination.Item>
                  )
              })}

              {totalPages.length > PAGES_PER_SET_NOTIFICATIONS && 
                  <Pagination.Next 
                    disabled={pageRangeNumber === (Math.ceil(totalPages.length / PAGES_PER_SET_NOTIFICATIONS))} 
                    onClick={() => nextPageSet(pageRangeNumber)} />}
          </Pagination>
      }

      {!isEmpty && <div className="bg-white shadow-md rounded-lg p-4">
        {paginatedNotifications.map((notification) => (
          <StudentNotificationItem notification={notification} updateCallback={refreshNotifications} />
        ))}
      </div>}
    </div>
  )
}

export default StudentNotificationsCenter
